/* @jsx jsx */
import { useEffect, useRef, useState } from 'react';
import { css, jsx } from '@emotion/react';

import {
  Colors,
  ColorName,
  Icon,
  Text,
  TooltipWrapper,
  TypographyName,
} from '../../../index';
import type { Props as BadgeProps } from '../../display/Badge/Badge';

import PageNavigationBadge from './PageNavigationBadge';
import PageNavigationRow from './PageNavigationRow';
import { ContentProps, OnSelectType, PageNavigationItem } from './types';
import checkIsTruncated from './utils/checkIsTruncated';

const Styles = {
  // The width value for textContainer is 90% of the row width, less the
  // width of the space available for the Badge count (32px).
  textContainer: (showCount: boolean) => [
    css`
      align-items: center;
      display: flex;
      flex-direction: row;
      position: relative;
      width: calc(90% - 32px);
    `,
    showCount === false &&
      css`
        width: 100%;
      `,
  ],
  textEllipsis: (textColor: ColorName) => css`
    color: ${Colors[textColor]};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
  rowContainer: (_hover: boolean, _selected: boolean) => [
    css`
      border-radius: 3px;
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      padding: 7px 20px;
    `,
  ],

  rowWrapper: (_hover: boolean, selected: boolean) => [
    selected &&
      css`
        border-radius: 3px;
        background-color: ${Colors.blue10};
      `,
  ],
};

export default function NonNestedRow({
  item,
  onSelect,
  showCount,
}: {
  item: PageNavigationItem;
  onSelect?: OnSelectType;
  showCount: boolean;
}) {
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef<HTMLElement>(null);

  useEffect(() => {
    setIsTruncated(checkIsTruncated(textRef));
  }, []);

  const InnerContent = ({ hover, selected }: ContentProps) => {
    const iconColor = hover || selected ? 'blue' : 'neutral600';
    const textColor: ColorName = hover || selected ? 'blue' : 'neutral800';
    const badgeColor = hover || selected ? 'info-bold' : 'white';
    const textType = selected ? 'paragraph3Semibold' : 'paragraph3';

    return (
      <div css={Styles.rowContainer(hover, selected)}>
        <span css={Styles.textContainer(showCount)}>
          {item.icon !== null && (
            <Icon
              color={iconColor as ColorName}
              name={item.icon || 'navFolder'}
              size={24}
            />
          )}
          <span css={Styles.textEllipsis(textColor)} ref={textRef}>
            <Text
              color={textColor}
              margin="0 0 0 8px"
              type={textType as TypographyName}
            >
              {item.title}
            </Text>
          </span>
        </span>
        {item.count ? (
          <PageNavigationBadge color={badgeColor as BadgeProps['color']}>
            {item.count.toString()}
          </PageNavigationBadge>
        ) : null}
      </div>
    );
  };

  const Content = ({ hover, selected }: ContentProps) => {
    const innerContent = isTruncated ? (
      <TooltipWrapper
        allowMultilineTooltip={true}
        content={item.title}
        isBlock={true}
        position="northeast"
      >
        {() => <InnerContent hover={hover} selected={selected} />}
      </TooltipWrapper>
    ) : (
      <InnerContent hover={hover} selected={selected} />
    );

    // Adding a wrapper here in order to properly set the background color in V5
    return <div css={Styles.rowWrapper(hover, selected)}>{innerContent}</div>;
  };

  return (
    <PageNavigationRow content={Content} item={item} onSelect={onSelect} />
  );
}
